import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { 
  User, Mail, Shield, Key, Lock, Smartphone, 
  ArrowLeft, Check, X, AlertTriangle, Calendar,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [passwordStatus, setPasswordStatus] = useState(null);
  
  // Edit mode states
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  
  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  
  // 2FA setup
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState('totp');
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [settingUp2FA, setSettingUp2FA] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setProfileData(response.data);
      setNewName(response.data.name);
    } catch (error) {
      console.error('Failed to load profile:', error.message);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPasswordStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/password-status');
      setPasswordStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch password status:', error.message);
    }
  }, []);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
    fetchPasswordStatus();
  }, [user, authLoading, navigate, fetchProfile, fetchPasswordStatus]);

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      // Note: You may need to add an endpoint for updating name
      toast.success('Name updated successfully');
      setEditingName(false);
      fetchProfile();
    } catch (error) { /* silent fail */
      toast.error('Failed to update name');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await apiClient.post('/auth/change-password', {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      setPasswordData({ current: '', new: '', confirm: '' });
      fetchPasswordStatus();
    } catch (error) { /* silent fail */
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleSetup2FA = async () => {
    setSettingUp2FA(true);
    try {
      const response = await apiClient.post('/auth/2fa/setup', { method: twoFAMethod });
      setTwoFASetupData(response.data);
    } catch (error) { /* silent fail */
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSettingUp2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    try {
      await apiClient.post('/auth/2fa/verify', { code: verificationCode });
      toast.success('2FA enabled successfully!');
      setShow2FASetup(false);
      setTwoFASetupData(null);
      setVerificationCode('');
      fetchProfile();
    } catch (error) { /* silent fail */
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    const code = prompt('Enter your 2FA code to disable:');
    if (!code) return;
    
    try {
      await apiClient.post('/auth/2fa/disable', { code });
      toast.success('2FA disabled successfully');
      fetchProfile();
    } catch (error) { /* silent fail */
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <User className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold">Profile</span>
          </Link>
          <div className="flex gap-4 items-center">
            <Link to="/dashboard">
              <Button variant="ghost" data-testid="nav-dashboard-btn">Dashboard</Button>
            </Link>
            <Link to="/classes">
              <Button variant="ghost" data-testid="nav-classes-btn">Classes</Button>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button variant="ghost" data-testid="nav-admin-btn">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Link */}
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          {/* Profile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-background-paper border border-white/10 rounded-sm p-8 mb-8"
          >
            <div className="flex items-center gap-6">
              {profileData?.picture ? (
                <img 
                  src={profileData.picture} 
                  alt={profileData.name} 
                  className="w-24 h-24 rounded-full"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center text-4xl font-bold">
                  {profileData?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="bg-background border-white/10 max-w-xs"
                      data-testid="edit-name-input"
                    />
                    <Button size="sm" onClick={handleUpdateName} data-testid="save-name-btn">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <h1 className="font-heading text-3xl font-bold mb-1" data-testid="profile-name">
                    {profileData?.name}
                  </h1>
                )}
                <p className="text-neutral-400 flex items-center gap-2" data-testid="profile-email">
                  <Mail className="w-4 h-4" />
                  {profileData?.email}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    profileData?.role === 'admin' 
                      ? 'bg-primary/20 text-primary' 
                      : 'bg-success/20 text-success'
                  }`} data-testid="profile-role">
                    {profileData?.role?.toUpperCase()}
                  </span>
                  {profileData?.two_fa_enabled && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-success/20 text-success flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      2FA ENABLED
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Security Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-background-paper border border-white/10 rounded-sm p-6"
            >
              <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                PASSWORD
              </h2>

              {/* Password Status */}
              {passwordStatus && !passwordStatus.exempt && (
                <div className={`p-4 rounded-sm mb-4 ${
                  passwordStatus.requires_change 
                    ? 'bg-error/10 border border-error/30' 
                    : passwordStatus.days_until_expiry <= 14
                      ? 'bg-warning/10 border border-warning/30'
                      : 'bg-success/10 border border-success/30'
                }`}>
                  {passwordStatus.requires_change ? (
                    <p className="text-error flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Password expired! Please change it now.
                    </p>
                  ) : (
                    <p className={passwordStatus.days_until_expiry <= 14 ? 'text-warning' : 'text-success'}>
                      <Calendar className="w-4 h-4 inline mr-2" />
                      Password expires in {passwordStatus.days_until_expiry} days
                    </p>
                  )}
                </div>
              )}

              {passwordStatus?.exempt && (
                <div className="p-4 rounded-sm mb-4 bg-neutral-800/50 border border-white/10">
                  <p className="text-neutral-400 text-sm">
                    Password expiry exempt (Google login or admin exemption)
                  </p>
                </div>
              )}

              {showPasswordChange ? (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label className="text-xs text-neutral-500">Current Password</Label>
                    <PasswordInput
                      value={passwordData.current}
                      onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                      className="bg-background border-white/10"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">New Password</Label>
                    <PasswordInput
                      value={passwordData.new}
                      onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                      className="bg-background border-white/10"
                      required
                      minLength={8}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">Confirm New Password</Label>
                    <PasswordInput
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                      className="bg-background border-white/10"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-primary" data-testid="save-password-btn">
                      Save Password
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowPasswordChange(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <Button 
                  onClick={() => setShowPasswordChange(true)} 
                  variant="outline"
                  className="w-full border-white/20"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              )}
            </motion.div>

            {/* Two-Factor Authentication */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-background-paper border border-white/10 rounded-sm p-6"
            >
              <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-primary" />
                TWO-FACTOR AUTH
              </h2>

              {profileData?.two_fa_enabled ? (
                <div>
                  <div className="p-4 rounded-sm mb-4 bg-success/10 border border-success/30">
                    <p className="text-success flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      2FA is enabled ({profileData.two_fa_method === 'totp' ? 'Authenticator App' : 'Email'})
                    </p>
                  </div>
                  <Button 
                    onClick={handleDisable2FA}
                    variant="outline"
                    className="w-full border-error text-error hover:bg-error hover:text-white"
                    data-testid="disable-2fa-btn"
                  >
                    Disable 2FA
                  </Button>
                </div>
              ) : show2FASetup ? (
                <div className="space-y-4">
                  {!twoFASetupData ? (
                    <>
                      <div>
                        <Label className="text-xs text-neutral-500 mb-2 block">Choose Method</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={twoFAMethod === 'totp' ? 'default' : 'outline'}
                            onClick={() => setTwoFAMethod('totp')}
                            className={twoFAMethod === 'totp' ? 'bg-primary' : 'border-white/20'}
                            data-testid="2fa-totp-btn"
                          >
                            <Smartphone className="w-4 h-4 mr-2" />
                            Authenticator
                          </Button>
                          <Button
                            type="button"
                            variant={twoFAMethod === 'email' ? 'default' : 'outline'}
                            onClick={() => setTwoFAMethod('email')}
                            className={twoFAMethod === 'email' ? 'bg-primary' : 'border-white/20'}
                            data-testid="2fa-email-btn"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleSetup2FA} 
                          className="bg-primary"
                          disabled={settingUp2FA}
                          data-testid="setup-2fa-btn"
                        >
                          {settingUp2FA ? 'Setting up...' : 'Setup 2FA'}
                        </Button>
                        <Button variant="ghost" onClick={() => setShow2FASetup(false)}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {twoFASetupData.method === 'totp' && twoFASetupData.qr_code && (
                        <div className="text-center">
                          <p className="text-sm text-neutral-400 mb-4">
                            Scan this QR code with your authenticator app
                          </p>
                          <img 
                            src={`data:image/png;base64,${twoFASetupData.qr_code}`}
                            alt="2FA QR Code"
                            className="mx-auto mb-4 bg-white p-2 rounded"
                            style={{ width: 200, height: 200 }}
                          />
                          <p className="text-xs text-neutral-500 mb-4">
                            Or enter manually: <code className="bg-background px-2 py-1 rounded">{twoFASetupData.secret}</code>
                          </p>
                        </div>
                      )}
                      {twoFASetupData.method === 'email' && (
                        <div className="p-4 bg-primary/10 border border-primary/30 rounded-sm mb-4">
                          <p className="text-sm">A verification code has been sent to your email.</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-xs text-neutral-500 mb-2 block">Enter 6-digit code</Label>
                        <Input
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="bg-background border-white/10 text-center text-2xl tracking-widest"
                          maxLength={6}
                          data-testid="2fa-code-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleVerify2FA} className="bg-success" data-testid="verify-2fa-btn">
                          Verify & Enable
                        </Button>
                        <Button variant="ghost" onClick={() => {
                          setShow2FASetup(false);
                          setTwoFASetupData(null);
                          setVerificationCode('');
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-neutral-400 mb-4">
                    Add an extra layer of security to your account by enabling two-factor authentication.
                  </p>
                  <Button 
                    onClick={() => setShow2FASetup(true)}
                    variant="outline"
                    className="w-full border-white/20"
                    data-testid="enable-2fa-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Enable 2FA
                  </Button>
                </div>
              )}
            </motion.div>
          </div>

          {/* Account Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-background-paper border border-white/10 rounded-sm p-6 mt-8"
          >
            <h2 className="font-heading text-xl font-bold mb-6">ACCOUNT INFORMATION</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">User ID</p>
                <p className="font-mono text-sm">{profileData?.user_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Account Created</p>
                <p className="text-sm">
                  {profileData?.created_at && format(new Date(profileData.created_at), 'PPP')}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Auth Type</p>
                <p className="text-sm capitalize">{profileData?.auth_type || 'Email'}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
